import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";
import { TurnDiff } from "../thread/diff";

/** `tool`, in the terminal register — one row, a status glyph in the gutter,
 * and the call's output hanging off a `⎿` continuation rule.
 *
 * The native skin folds the output behind a disclosure because a web reader
 * scrolls a document and an expanding row costs a scroll position. A terminal
 * transcript has no such contract: output is simply THERE, indented under the
 * call that produced it, and a reader skims past it by moving their eye. So
 * this renders `detail` inline and treats `expanded` as the caller's hint
 * rather than a control it owns.
 *
 * `verb` wins over `tool` exactly as the contract says: the row shows one
 * word, never both. That rule is what lets a vendor invent a tenth call type
 * without either skin changing, and it is worth restating here because a
 * monospace row is the place where the temptation to print both is strongest
 * — there is room. Resisting it is what keeps the two skins saying the same
 * thing. */
@component({ name: "std-term-turn-tool" })
export class TermTurnTool extends LonaWebComponent {
  private $mark: HTMLElement = this.$("mark");
  private $name: HTMLElement = this.$("name");
  private $title: HTMLElement = this.$("title");
  private $note: HTMLElement = this.$("note");
  private $detail: HTMLElement = this.$("detail");

  /** Gutter glyphs. Deliberately the marks a tsh trace already uses, so an
   * operator reading the web surface and the terminal side by side is not
   * learning two alphabets for one idea. */
  static readonly MARK: Record<TurnSlots.ToolStatus, string> = {
    pending: "○",
    running: "◐",
    done: "⏺",
    failed: "✕",
    declined: "⊘",
  };

  static readonly TONE: Record<TurnSlots.ToolStatus, string> = {
    pending: "var(--text-ter, #8b93a7)",
    running: "var(--fg-blue, #3b82f6)",
    done: "var(--fg-green, #2da44e)",
    failed: "var(--fg-red, #e5484d)",
    declined: "var(--fg-yellow, #d4a72c)",
  };

  /** One span per line, because a `<pre>` carries text and a diff carries
   * three kinds of line. Classification is `TurnDiff.classify`, the shared
   * hunk-aware pass — first-character rules misread `--- a/x` inside a hunk
   * and `-1 file changed` outside one; this skin keeps the raw line and
   * spends only colour. */
  private static diffLines(text: string): Node[] {
    return TurnDiff.classify(text.split("\n")).map((row, index) => {
      const $line = document.createElement("span");
      $line.textContent = index === 0 ? row.raw : `\n${row.raw}`;
      $line.style.color = TermTurnTool.DIFF_KIND_TONE[row.kind];
      return $line;
    });
  }

  static readonly DIFF_KIND_TONE: Record<TurnDiff.Kind, string> = {
    add: "var(--fg-green, #2da44e)",
    del: "var(--fg-red, #e5484d)",
    ctx: "var(--text-sec, #57606a)",
    hunk: "var(--text-ter, #8b93a7)",
    meta: "var(--text-ter, #8b93a7)",
  };

  static makeWith(props: TurnSlots.Props["tool"]): TermTurnTool {
    return (TermTurnTool.make() as TermTurnTool).bind(props);
  }

  bind(props: TurnSlots.Props["tool"]): this {
    this.$mark.textContent = TermTurnTool.MARK[props.status];
    this.$mark.style.color = TermTurnTool.TONE[props.status];

    // One word, never both — `verb` is the dialect's own wording and wins.
    const name = props.verb !== undefined && props.verb !== "" ? props.verb : props.tool;
    this.$name.textContent = name;
    this.$name.style.display = name === "" ? "none" : "";

    this.$title.textContent = props.title;

    const note = props.note ?? "";
    this.$note.textContent = note === "" ? "" : `(${note})`;
    this.$note.style.display = note === "" ? "none" : "";

    const detail = props.detail?.text ?? "";
    // Colour is the only diff treatment the terminal register has room for,
    // and it is the one the terminal already uses — the cell grid cannot
    // indent or band a patch without spending columns the row needs.
    if (detail !== "" && props.detail?.language === "diff") {
      this.$detail.replaceChildren(...TermTurnTool.diffLines(detail));
    } else {
      this.$detail.textContent = detail;
    }
    this.$detail.style.display = detail === "" ? "none" : "";
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-pri, #1f2328);
      }
      #row {
        display: flex;
        align-items: baseline;
        gap: 6px;
      }
      /* A fixed-width gutter is what makes a column of these rows scan: the
         glyph never shifts the text beside it, whatever the status. */
      #mark {
        flex: none;
        width: 1ch;
        text-align: center;
      }
      #name {
        flex: none;
        font-weight: 600;
      }
      #title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--text-sec, #5c6370);
      }
      #note {
        flex: none;
        color: var(--text-ter, #8b93a7);
      }
      /* Output hangs under the call, indented past the gutter so the rule
         lines up with the glyph column above it. */
      #detail {
        margin: 2px 0 0 0;
        padding-left: 2ch;
        border-left: 1px solid var(--divider, #d8dee9);
        margin-left: 0.5ch;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        color: var(--text-sec, #5c6370);
        max-height: 18em;
        overflow-y: auto;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="row">
      <span id="mark"></span>
      <span id="name"></span>
      <span id="title"></span>
      <span id="note"></span>
    </div>
    <pre id="detail"></pre>
  `;
}
